import { FC, useRef, useEffect } from 'react';
import { Folder, Book, Settings, ArrowLeft, Home } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Custom Handlers Hook
import { useDashboardHandlers } from '@/features/dashboard/hooks/useDashboardHandlers';
import { useSyncStore } from '@/stores/useSyncStore';

// Sub-components
import { ProjectsView } from '../features/project-management/ui/ProjectsView';
import { CatalogView } from '../features/database/ui/CatalogView';
import { SystemView } from '../features/migration/ui/SystemView';

// Modals
import { ConfirmationModal } from '@/shared/ui/ConfirmationModal';
import { CreateServiceModal } from '@/features/database/ui/modals/CreateServiceModal';
import { CreateUnitModal } from '@/features/database/ui/modals/CreateUnitModal';
import { ImportPreviewModal } from '@/features/dashboard/ui/ImportPreviewModal';
import { LanguageSelector } from '@/shared/ui/LanguageSelector';
import { SessionWidget, PasswordNudgeBanner } from '@/features/auth';
import { TrialBanner } from '@/features/billing';
import { InviteWidget } from '@/features/referral';

const DashboardPage: FC = () => {
    const {
        t,
        navigate,
        activeTab,
        setActiveTab,
        database,
        projectLibrary,
        newProjectName,
        setNewProjectName,
        editingProjectId,
        setEditingProjectId,
        editingProjectName,
        setEditingProjectName,
        isCreateServiceOpen,
        setIsCreateServiceOpen,
        isCreateUnitOpen,
        setIsCreateUnitOpen,
        isResetConfirmOpen,
        setIsResetConfirmOpen,
        projectIdToDelete,
        setProjectIdToDelete,
        importPreview,
        openModal,

        // Handlers
        handleOpenProject,
        handleConfirmDeleteProject,
        handleSaveCurrent,
        handleRenameProject,
        handleFullBackup,
        handleFullRestore,
        handleImportPriceList,
        handleConfirmImport,
        handleCancelImport,
        handleExportPriceList,
        handleResetSystem
    } = useDashboardHandlers();

    // SYNC-03 (UI-SPEC backstop row 8): read the login-time hydration signal so
    // ProjectsView can withhold the empty-state card while a second device's
    // projects are still arriving. Selector-only — no other DashboardPage
    // behaviour changes.
    const hydrating = useSyncStore((s) => s.hydrating);

    const containerRef = useRef<HTMLDivElement>(null);

    const tabs = [
        { id: 'projects', label: t('dashboard.tabs.projects', 'Проекты'), icon: Folder },
        { id: 'catalog', label: t('dashboard.tabs.catalog', 'Справочник'), icon: Book },
        { id: 'system', label: t('dashboard.tabs.system', 'Система'), icon: Settings },
    ];

    const handleTablistKeyDown = (e: React.KeyboardEvent) => {
        const currentIndex = tabs.findIndex(t => t.id === activeTab);
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;

        switch (e.key) {
            case 'ArrowRight':
                nextIndex = (currentIndex + 1) % tabs.length;
                e.preventDefault();
                break;
            case 'ArrowLeft':
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                e.preventDefault();
                break;
            case 'Home':
                nextIndex = 0;
                e.preventDefault();
                break;
            case 'End':
                nextIndex = tabs.length - 1;
                e.preventDefault();
                break;
            default:
                return;
        }

        const nextTab = tabs[nextIndex];
        if (nextTab) {
            setActiveTab(nextTab.id as 'projects' | 'catalog' | 'system');
        }
    };

    useEffect(() => {
        if (activeTab && containerRef.current) {
            const activeEl = containerRef.current.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
            if (activeEl && document.activeElement !== activeEl) {
                activeEl.focus();
            }
        }
    }, [activeTab]);

    return (
        <div className="flex flex-col h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 overflow-hidden transition-colors duration-300">
            {/* CLEAN HEADER */}
            <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 shrink-0 z-40">
                <div className="flex items-center gap-4">
                    <motion.button 
                        whileHover={{ scale: 1.05, x: -5 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/editor')}
                        className="flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors font-bold text-sm"
                    >
                        <ArrowLeft size={20}/>
                        <span className="hidden sm:inline">{t('dashboard.back_to_editor', 'В редактор')}</span>
                    </motion.button>
                    
                    <div className="h-6 w-[1px] bg-slate-200 dark:bg-slate-800 hidden sm:block"></div>
                    
                    <div className="flex items-center gap-2">
                        <div className="bg-blue-600 p-1.5 rounded-lg shadow-lg shadow-blue-200 dark:shadow-none hidden xs:block">
                            <Home className="text-white" size={16} />
                        </div>
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        <h1 className="font-extrabold text-lg tracking-tight hidden xs:block">Estimatix</h1>
                    </div>
                </div>

                {/* UNIVERSAL TAB NAVIGATION */}
                <div 
                    ref={containerRef}
                    role="tablist"
                    aria-label={t('dashboard.navigation', 'Навигация панели управления')}
                    onKeyDown={handleTablistKeyDown}
                    className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl gap-1"
                >
                    {tabs.map(tab => (
                        <button 
                            key={tab.id}
                            id={`dashboard-tab-${tab.id}`}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            aria-controls={`dashboard-panel-${tab.id}`}
                            tabIndex={activeTab === tab.id ? 0 : -1}
                            onClick={() => setActiveTab(tab.id as 'projects' | 'catalog' | 'system')}
                            className={`relative px-3 md:px-6 py-1.5 rounded-lg transition-all flex items-center gap-2 ${activeTab === tab.id ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                        >
                            {activeTab === tab.id && (
                                <motion.div 
                                    layoutId="active-dash-tab"
                                    className="absolute inset-0 bg-white dark:bg-slate-700 rounded-lg shadow-sm"
                                    transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                                />
                            )}
                            <tab.icon size={16} className="relative z-10" />
                            <span className="text-xs font-bold relative z-10 hidden md:inline">{tab.label}</span>
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    <TrialBanner />
                    <SessionWidget />
                    <LanguageSelector />
                </div>
            </header>

            <main className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-slate-950 px-4 md:px-10 pb-32 pt-6 md:pt-10 relative scroll-smooth">
                <div className="max-w-[1400px] mx-auto">
                    {/* Phase 43 (PWD-08): account-level nudge, not scoped to any one tab —
                        rendered above the tab panels so it stays visible regardless of activeTab. */}
                    <PasswordNudgeBanner onNavigateToSettings={() => setActiveTab('system')} />
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            id={`dashboard-panel-${activeTab}`}
                            role="tabpanel"
                            aria-labelledby={`dashboard-tab-${activeTab}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            {activeTab === 'projects' && (
                                <>
                                    {/* REFUI-01: InviteWidget — collapsed by default (§10 optional) */}
                                    <InviteWidget />
                                    <ProjectsView
                                        library={projectLibrary.library}
                                        onOpen={handleOpenProject}
                                        onDeleteRequest={setProjectIdToDelete}
                                        onRename={handleRenameProject}
                                        onSaveCurrent={handleSaveCurrent}
                                        newProjectName={newProjectName}
                                        setNewProjectName={setNewProjectName}
                                        editingId={editingProjectId}
                                        setEditingId={setEditingProjectId}
                                        editName={editingProjectName}
                                        setEditName={setEditingProjectName}
                                        hydrating={hydrating}
                                    />
                                </>
                            )}

                            {activeTab === 'catalog' && (
                                <CatalogView 
                                    services={database.services}
                                    categories={database.categories}
                                    units={database.units}
                                    recipes={database.recipes}
                                    updateService={database.updateService}
                                    deleteService={database.deleteService}
                                    updateCategory={database.updateCategory}
                                    deleteCategory={database.deleteCategory}
                                    updateUnit={database.updateUnit}
                                    deleteUnit={database.deleteUnit}
                                    onEditRecipe={(recipe, cat) => openModal('recipe_editor', { id: recipe.id, category: cat })}
                                    onDeleteRecipe={(id, cat) => openModal('recipe_delete_confirm', { id, category: cat })}
                                    onCreateService={() => setIsCreateServiceOpen(true)}
                                    onCreateCategory={() => database.createCategory(t('dashboard.new_category_default', 'Новая категория'))}
                                    onCreateUnit={() => setIsCreateUnitOpen(true)}
                                    onCreateRecipe={(cat) => {
                                        const newRecipe = database.createNewRecipe(cat);
                                        if (newRecipe) openModal('recipe_editor', { id: newRecipe.id, category: cat, initialRecipe: newRecipe });
                                    }}
                                />
                            )}

                            {activeTab === 'system' && (
                                <SystemView 
                                    onFullBackup={handleFullBackup}
                                    onFullRestore={handleFullRestore}
                                    onResetSystem={() => setIsResetConfirmOpen(true)}
                                    onImportPriceList={handleImportPriceList}
                                    onExportPriceList={handleExportPriceList}
                                />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>

            {isCreateServiceOpen && (
                <CreateServiceModal 
                    isOpen={isCreateServiceOpen}
                    onClose={() => setIsCreateServiceOpen(false)}
                    onCreate={(data) => database.createService(data)}
                    categories={database.categories}
                    units={database.units}
                    onCreateCategory={() => database.createCategory(t('dashboard.new_category_default', 'Новая категория'))}
                />
            )}
            {isCreateUnitOpen && (
                <CreateUnitModal
                    isOpen={isCreateUnitOpen}
                    onClose={() => setIsCreateUnitOpen(false)}
                    onCreate={(name: string) => database.createUnit(name)}
                />
            )}
            <ConfirmationModal 
                isOpen={isResetConfirmOpen}
                onClose={() => setIsResetConfirmOpen(false)}
                onConfirm={handleResetSystem}
                title={t('dashboard.reset_confirm.title', 'Полный сброс')}
                message={t('dashboard.reset_confirm.message', 'Вы уверены? Все данные будут уничтожены.')}
            />

            <ConfirmationModal
                isOpen={projectIdToDelete !== null}
                onClose={() => setProjectIdToDelete(null)}
                onConfirm={handleConfirmDeleteProject}
                title={t('projects.delete_modal_title', 'Удалить проект')}
                message={t('projects.delete_confirm', 'Вы уверены, что хотите удалить этот проект? Это действие нельзя отменить.')}
            />

            <ImportPreviewModal
                isOpen={importPreview !== null}
                onClose={handleCancelImport}
                onConfirm={handleConfirmImport}
                stats={importPreview?.stats ?? null}
            />
        </div>
    );
};

export default DashboardPage;
